/**
 * openPoExcel.ts
 *
 * Real Excel (.xlsx) export utility for Open Purchase Orders report.
 */
import * as XLSX from 'xlsx'
import type { OpenPoRow } from '@/services/reports/reportsApi'
import { formatDateFull } from '@/lib/dateFormat'

const DELIVERY_LABEL: Record<string, string> = {
  NOT_RECEIVED: 'Chưa nhận',
  PARTIALLY_RECEIVED: 'Giao một phần',
  FULLY_RECEIVED: 'Đã nhận đủ',
}

const WORKFLOW_LABEL: Record<string, string> = {
  APPROVED: 'Đã duyệt',
  SENT: 'Đã gửi NCC',
  PARTIAL: 'Đang nhận hàng',
  SHORT: 'Thiếu hàng',
}

export function buildOpenPoExcelWorkbook(items: OpenPoRow[]): XLSX.WorkBook {
  const wb = XLSX.utils.book_new()

  // Sheet 1: Tổng hợp PO
  const poHeaders = [
    'STT',
    'Mã PO',
    'Nhà cung cấp',
    'Số dòng hàng',
    'Tổng tiền',
    '% Đã nhận',
    'Dự kiến giao',
    'Tình trạng giao',
    'Trạng thái PO',
    'Quá hạn',
  ]
  const poAoa: (string | number)[][] = [poHeaders]
  items.forEach((po, idx) => {
    poAoa.push([
      idx + 1,
      po.code,
      po.supplier_name,
      po.line_count,
      po.total_amount,
      po.received_pct,
      po.delivery_date ? formatDateFull(po.delivery_date) : '',
      DELIVERY_LABEL[po.delivery_status] ?? po.delivery_status,
      WORKFLOW_LABEL[po.workflow_status] ?? po.workflow_status,
      po.is_overdue ? 'Quá hạn' : '',
    ])
  })
  const poWs = XLSX.utils.aoa_to_sheet(poAoa)
  poWs['!cols'] = [
    { wch: 6 },
    { wch: 16 },
    { wch: 28 },
    { wch: 14 },
    { wch: 16 },
    { wch: 12 },
    { wch: 16 },
    { wch: 18 },
    { wch: 16 },
    { wch: 12 },
  ]
  XLSX.utils.book_append_sheet(wb, poWs, 'Tổng hợp PO')

  // Sheet 2: Chi tiết dòng hàng
  const lineHeaders = [
    'STT',
    'Mã PO',
    'Nhà cung cấp',
    'STT dòng',
    'Mã vật tư',
    'Tên vật tư',
    'Quy cách',
    'ĐVT',
    'Đã đặt',
    'Đã nhận',
    'Còn lại',
    'Tình trạng giao',
    'Thành tiền',
    'Dự kiến giao',
  ]
  const lineAoa: (string | number)[][] = [lineHeaders]
  let overallLineIdx = 1
  items.forEach((po) => {
    po.lines.forEach((l) => {
      lineAoa.push([
        overallLineIdx++,
        po.code,
        po.supplier_name,
        l.sort_order,
        l.material_code || '',
        l.material_name,
        l.spec_text || '',
        l.order_uom,
        l.ordered_qty,
        l.received_qty,
        l.remaining_qty,
        DELIVERY_LABEL[l.delivery_status] ?? l.delivery_status,
        l.line_total,
        po.delivery_date ? formatDateFull(po.delivery_date) : '',
      ])
    })
  })
  const lineWs = XLSX.utils.aoa_to_sheet(lineAoa)
  lineWs['!cols'] = [
    { wch: 6 },
    { wch: 16 },
    { wch: 28 },
    { wch: 10 },
    { wch: 16 },
    { wch: 30 },
    { wch: 24 },
    { wch: 8 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
  ]
  XLSX.utils.book_append_sheet(wb, lineWs, 'Chi tiết dòng hàng')

  return wb
}

export function exportOpenPoToExcel(items: OpenPoRow[]): void {
  const wb = buildOpenPoExcelWorkbook(items)
  const todayStr = new Date().toISOString().slice(0, 10)
  const filename = `Báo cáo PO đang mở_${todayStr}.xlsx`
  XLSX.writeFile(wb, filename)
}
